import React, { useEffect, useState } from "react";
import axios from "axios";
import { useRequestContext } from "../../hooks/useRequest";
import { Collapsible, PdfShower } from "../../../components";
import { SectionTrigger } from "../../../orderManagement/molecules/SectionTrigger";
import { pxSellSectionId } from "../../v2/sectionConfig";
import { AgreementFooter } from "./AgreementFooter";

export function ConsignmentAgreement({ step, idx, navigateTo }) {
  const [fileId, setFileId] = useState(null);
  // Sensitive (seller PII) → backend gates GET /api/pdfs/{id}; a token-less
  // <object> request 403s. Render the temporary signed URL the backend now
  // returns; keep numeric fileId for the send-agreement call.
  const [pdfUrl, setPdfUrl] = useState(null);
  const {
    getContext,
    onConsignmentAgreementSend,
    toggleStep,
    refreshRequest,
  } = useRequestContext();
  // Step-aware so a PX group's per-sell-request instance generates the
  // agreement for ITS trade-in, not for the parent order request.
  const { request } = getContext(idx, step);
  const [isLoading, setIsLoading] = useState(false);

  const getPDF = async () => {
    const { data } = await axios.get(`/api/requests/${request.id}/pdf-consign`);
    setFileId(data.file_id);
    setPdfUrl(data.signed_url || null);
    // The backend stamps consignment_agreement_sent_at on the first
    // PDF generation. Refetch the request so the V2 sidebar's
    // Consignment Agreement step picks up the new timestamp and flips
    // green without waiting for a manual reload.
    await refreshRequest?.();
  };

  // Save and Send both end the rep's interaction with this section;
  // chain navigateTo so the V2 nav lands on the next sell-flow step. The
  // static id only exists outside a PX slot — inside one the sections are
  // suffixed per trade-in, and a target that does not exist bounces the rep
  // back to the first visible section.
  const advanceToPurchasing = () =>
    navigateTo?.(pxSellSectionId("purchasing_agreement", step?.pxSellIdx));

  const onSave = () => {
    onConsignmentAgreementSend();
    advanceToPurchasing();
  };

  const onSend = async () => {
    setIsLoading(true);
    await axios.post(
      `/api/requests/${request.id}/send-consignment-agreement/${fileId}`
    );
    setIsLoading(false);
    onConsignmentAgreementSend();
    advanceToPurchasing();
    await refreshRequest?.();
  };

  useEffect(() => {
    getPDF();
  }, []);

  return (
    <Collapsible
      trigger={<SectionTrigger label={step.label} icon="doc" />}
      open={step.open}
      handleTriggerClick={() => toggleStep(idx)}
      testId="confirm-request"
    >
      <div className="mt-2">
        <PdfShower fileId={pdfUrl || fileId} />
      </div>
      <AgreementFooter
        feature="consignment"
        className="my-2"
        onSave={onSave}
        onSend={onSend}
        sending={isLoading}
      />
    </Collapsible>
  );
}
